import { useEffect, useState, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { districtsData } from "../../utils/district-data";
import tokenService from "../../services/token-service";
import { apiRequest } from "../../services/api-client";
import cn from "classnames";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import LoginModal from "../auth/login-modal";
import RegisterModal from "../auth/register-modal";
import { useNavigate } from "react-router-dom";

const FILE_SIZE = 5 * 1024 * 1024; // 5MB
const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

// Calculate max date for 18+ years restriction
const getMaxDate = () => {
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return maxDate.toISOString().split("T")[0];
};

const validationSchema = Yup.object({
  fullName: Yup.string()
    .required("Full name is required")
    .min(3, "Name must be at least 3 characters")
    .max(50, "Name must not exceed 50 characters"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]{11}$/, "Phone number must be 11 digits"),
  dob: Yup.date()
    .required("Date of birth is required")
    .max(new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()),
      "You must be at least 18 years old")
    .min(new Date(1900, 0, 1), "Invalid date of birth"),
  cnic: Yup.string()
    .required("CNIC is required")
    .matches(/^\d{5}-\d{7}-\d$/, "CNIC must be in format: xxxxx-xxxxxxx-x"),
  address: Yup.string()
    .required("Address is required")
    .min(10, "Address is too short"),
  district: Yup.string().required("District is required"),
  tehsil: Yup.string().required("Tehsil is required"),
  image: Yup.mixed()
    .nullable()
    .test("fileSize", "File size is too large", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return value.size <= FILE_SIZE;
    })
    .test("fileFormat", "Unsupported file format", (value) => {
      if (!value) return true;
      if (typeof value === "string") return true;
      return SUPPORTED_FORMATS.includes(value.type);
    }),
});

const ProfileSetupComponent = ({ onNext }) => {
  const user = useMemo(() => tokenService.getUserDetails() || {}, []);
  const navigate = useNavigate();
  const [districtOptions, setDistrictOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState(null); // null | 'available' | 'taken'
  const [emailCheckTimer, setEmailCheckTimer] = useState(null);
  const [emailCheckedOnce, setEmailCheckedOnce] = useState(false);

  // Modal states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Check if user is logged in
  const isUserLoggedIn = useMemo(() => {
    const authUser = localStorage.getItem("authUser");
    return authUser && authUser !== "null" && authUser !== "undefined";
  }, []);

  // Get fresh user data from localStorage
  const getAuthUser = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser && authUser !== "null" && authUser !== "undefined") {
        return JSON.parse(authUser);
      }
    } catch (error) {
      console.error("Error parsing authUser:", error);
    }
    return null;
  };

  const authUser = getAuthUser();

  // Check which fields should be disabled
  // Disable: fullName, email, phoneNumber, dob if they exist
  // CNIC: Only disable if it exists (user has filled it before)
  // Address, District, Tehsil: Always enabled (can be updated)
  const shouldDisableField = (fieldName) => {
    if (!authUser) return false;

    switch (fieldName) {
      case 'fullName':
      case 'email':
      case 'phoneNumber':
      case 'dob':
        return !!authUser[fieldName];
      case 'cnic':
        return !!authUser.cnic; // Only disable if CNIC already exists
      default:
        return false;
    }
  };

  useEffect(() => {
    // Show auth prompt if user is not logged in
    if (!isUserLoggedIn) {
      setShowAuthPrompt(true);
      return;
    }

    setDistrictOptions(Object.keys(districtsData));

    // Load user data from authUser in localStorage
    if (authUser) {
      // Set formik values from authUser
      if (authUser.fullName) formik.setFieldValue("fullName", authUser.fullName);
      if (authUser.email) {
        formik.setFieldValue("email", authUser.email);
        setEmailCheckStatus('available');
        setEmailCheckedOnce(true);
      }
      if (authUser.phoneNumber) formik.setFieldValue("phoneNumber", authUser.phoneNumber);
      if (authUser.dob) {
        const dobDate = new Date(authUser.dob).toISOString().split("T")[0];
        formik.setFieldValue("dob", dobDate);
      }
      if (authUser.cnic) formik.setFieldValue("cnic", authUser.cnic);
      if (authUser.address) formik.setFieldValue("address", authUser.address);
      if (authUser.district) formik.setFieldValue("district", authUser.district);
      if (authUser.tehsil) formik.setFieldValue("tehsil", authUser.tehsil);
      if (authUser.image) formik.setFieldValue("image", authUser.image);
    }

    // Also check saved form data from localStorage (for unsaved changes)
    const savedFormData = localStorage.getItem("jobApplyFormData");
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        // Only load fields that are not disabled
        Object.keys(parsedData).forEach(key => {
          if (key !== 'image' && parsedData[key] && !shouldDisableField(key)) {
            formik.setFieldValue(key, parsedData[key]);
          }
        });
      } catch (error) {
        console.error("Error loading saved form data:", error);
      }
    }
  }, [isUserLoggedIn]);

  const checkEmailAvailability = async (email, setFieldError) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCheckStatus(null);
      return;
    }

    // Don't check if already checked once or if it's user's own email
    if (emailCheckedOnce || (authUser?.email && authUser.email === email)) {
      return;
    }

    setIsCheckingEmail(true);
    setEmailCheckStatus(null);

    try {
      const response = await apiRequest("GET", `/check-email?email=${encodeURIComponent(email)}`);

      if (response.data.isRegistered) {
        setEmailCheckStatus('taken');
        if (setFieldError) {
          setFieldError("email", "Email is already registered");
        }
        toast.error("Email is already registered", { id: 'email-check' });
      } else {
        setEmailCheckStatus('available');
        setEmailCheckedOnce(true);
        toast.success("Email is available!", { id: 'email-check' });
      }
    } catch (error) {
      console.error("Error checking email:", error);
      setEmailCheckStatus(null);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      fullName: authUser?.fullName || "",
      email: authUser?.email || "",
      phoneNumber: authUser?.phoneNumber || "",
      dob: authUser?.dob ? new Date(authUser.dob).toISOString().split("T")[0] : "",
      cnic: authUser?.cnic || "",
      address: authUser?.address || "",
      district: authUser?.district || "",
      tehsil: authUser?.tehsil || "",
      image: authUser?.image || null,
    },
    validationSchema,
    onSubmit: async (values) => {
      // Check if email is taken before submitting
      if (emailCheckStatus === 'taken') {
        toast.error("Please use a different email address");
        return;
      }

      setIsSubmitting(true);
      try {
        // Save form data to localStorage
        const dataToSave = { ...values };
        delete dataToSave.image;
        localStorage.setItem("jobApplyFormData", JSON.stringify(dataToSave));

        onNext(values);
      } catch (error) {
        toast.error("Failed to save profile information");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Auto-check email after user stops typing (debounced) - only for new emails
  useEffect(() => {
    if (emailCheckTimer) {
      clearTimeout(emailCheckTimer);
    }

    const email = formik.values.email;

    // Skip email check if:
    // 1. Already checked once
    // 2. It's the user's own email from authUser
    // 3. Field is disabled (pre-populated)
    if (emailCheckedOnce ||
      (authUser?.email && email === authUser.email) ||
      shouldDisableField('email')) {
      return;
    }

    // Only check if email is new and not already checked
    if (email && email.includes('@')) {
      const timer = setTimeout(() => {
        checkEmailAvailability(email, formik.setFieldError);
      }, 3000);

      setEmailCheckTimer(timer);
    }

    return () => {
      if (emailCheckTimer) {
        clearTimeout(emailCheckTimer);
      }
    };
  }, [formik.values.email, emailCheckedOnce]);

  // Save form data on field change
  useEffect(() => {
    if (formik.dirty) {
      const dataToSave = { ...formik.values };
      delete dataToSave.image;
      localStorage.setItem("jobApplyFormData", JSON.stringify(dataToSave));
    }
  }, [formik.values]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > FILE_SIZE) {
        toast.error("File size should not exceed 5MB");
        return;
      }

      if (!SUPPORTED_FORMATS.includes(file.type)) {
        toast.error("Please upload a valid image file (JPG, JPEG, PNG)");
        return;
      }

      formik.setFieldValue("image", file);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setShowAuthPrompt(false);
    window.location.reload(); // Reload to get fresh user data
  };

  const handleRegisterSuccess = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true); // Switch to login after registration
  };

  // Render modals outside main content
  const renderModals = () => (
    <>
      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Authentication Required
            </h2>
            <p className="text-gray-600 mb-6 text-center">
              Please log in if you're already registered, or create an account before applying.
            </p>
            <div className="space-y-3">
              {/* <button
                onClick={() => {
                  setShowAuthPrompt(false);
                  setShowLoginModal(true);
                }}
                className="w-full bg-[#F5A623] text-white py-3 rounded-lg font-semibold hover:bg-[#F5A623]/90 transition-colors"
              >
                Login
              </button>
              <button
                onClick={() => {
                  setShowAuthPrompt(false);
                  setShowRegisterModal(true);
                }}
                className="w-full bg-white text-[#F5A623] py-3 rounded-lg font-semibold border-2 border-[#F5A623] hover:bg-[#F5A623] hover:text-white transition-colors"
              >
                Register
              </button> */}
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="w-full bg-[#F5A623] text-white py-3 rounded-lg font-semibold hover:bg-[#F5A623]/90 transition-colors"
              >
                Login
              </button>

              <button
                type="button"
                onClick={() => navigate("/register", { replace: true })}
                className="w-full bg-white text-[#F5A623] py-3 rounded-lg font-semibold border-2 border-[#F5A623] hover:bg-[#F5A623] hover:text-white transition-colors"
              >
                Register
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        onSwitchToSignup={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegisterSuccess}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
    </>
  );

  // Show blank screen if not authenticated
  if (!isUserLoggedIn) {
    return renderModals();
  }

  return (
    <>
      {renderModals()}
      <Card>
        <CardContent className="p-6">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Full Name"
                name="fullName"
                placeholder="Enter your full name"
                formik={formik}
                disabled={shouldDisableField('fullName')}
              />

              {/* Email Field with Auto-check */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    disabled={shouldDisableField('email')}
                    className={cn(
                      formik.errors.email && formik.touched.email && "border-red-500",
                      shouldDisableField('email') && "bg-gray-100 cursor-not-allowed opacity-60"
                    )}
                  />
                  {/* Loader inside email field */}
                  {isCheckingEmail && !shouldDisableField('email') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-5 w-5 animate-spin text-[#F5A623]" />
                    </div>
                  )}
                  {/* Success checkmark */}
                  {emailCheckStatus === 'available' && !isCheckingEmail && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                  )}
                  {/* Error icon */}
                  {emailCheckStatus === 'taken' && !isCheckingEmail && (
                    <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                  )}
                </div>
                {formik.errors.email && formik.touched.email && (
                  <div className="text-red-500 text-sm">{formik.errors.email}</div>
                )}
                {emailCheckStatus === 'available' && !isCheckingEmail && !shouldDisableField('email') && (
                  <div className="text-green-600 text-sm flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Email is available
                  </div>
                )}
              </div>

              <FormField
                label="Date of Birth"
                name="dob"
                type="date"
                formik={formik}
                max={getMaxDate()}
                disabled={shouldDisableField('dob')}
              />
              <FormField
                label="Phone Number"
                name="phoneNumber"
                placeholder="Enter your phone number"
                formik={formik}
                disabled={shouldDisableField('phoneNumber')}
              />
              <FormField
                label="CNIC Number"
                name="cnic"
                placeholder="xxxxx-xxxxxxx-x"
                formik={formik}
                disabled={shouldDisableField('cnic')}
              />
            </div>
            <FormField
              label="Address"
              name="address"
              placeholder="Enter your complete address"
              formik={formik}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="district">District</Label>
                <Select
                  value={formik.values.district || undefined}
                  onValueChange={(value) => {
                    formik.setFieldValue("district", value);
                    formik.setFieldValue("tehsil", "");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select District">
                      {formik.values.district || "Select District"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {districtOptions.map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formik.errors.district && formik.touched.district && (
                  <div className="text-red-500 text-sm">
                    {formik.errors.district}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tehsil">Tehsil</Label>
                <Select
                  value={formik.values.tehsil || undefined}
                  onValueChange={(value) => formik.setFieldValue("tehsil", value)}
                  disabled={!formik.values.district}
                >
                  <SelectTrigger className={!formik.values.district ? "bg-gray-100 cursor-not-allowed opacity-60" : ""}>
                    <SelectValue placeholder="Select Tehsil">
                      {formik.values.tehsil || "Select Tehsil"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(districtsData[formik.values.district] || []).map(
                      (tehsil) => (
                        <SelectItem key={tehsil} value={tehsil}>
                          {tehsil}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {formik.errors.tehsil && formik.touched.tehsil && (
                  <div className="text-red-500 text-sm">
                    {formik.errors.tehsil}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image">Profile Image (Optional)</Label>
              <Input
                id="image"
                name="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="cursor-pointer"
              />
              {formik.errors.image && formik.touched.image && (
                <div className="text-red-500 text-sm">{formik.errors.image}</div>
              )}
              {formik.values.image && (
                <div className="mt-2">
                  <img
                    src={
                      typeof formik.values.image === "string"
                        ? formik.values.image
                        : URL.createObjectURL(formik.values.image)
                    }
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded-full border"
                  />
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-[#F5A623] hover:bg-[#F5A623]/90"
              disabled={isSubmitting || emailCheckStatus === 'taken'}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                "Next"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );
};

const FormField = ({ label, name, placeholder, type = "text", formik, disabled = false, max }) => {
  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Input
        type={type}
        id={name}
        name={name}
        placeholder={placeholder}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[name]}
        disabled={disabled}
        max={max}
        className={cn(
          formik.errors[name] && formik.touched[name] && "border-red-500",
          disabled && "bg-gray-100 cursor-not-allowed opacity-60"
        )}
      />
      {formik.errors[name] && formik.touched[name] && (
        <div className="text-red-500 text-sm">{formik.errors[name]}</div>
      )}
    </div>
  );
};

export default ProfileSetupComponent;